import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { AddCommentAction, DeleteCommentAction } from '@dwo/actions';
import { AddEditCommentModel, CommentContext } from '@app/work-order/comments/add-comment.model';
import { CommentDto } from '@app/generated';
import { WorkOrderCommentsService } from '@app/work-order/work-order-comments.service';
import { LoggingService } from '@app/shared/services/logging.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmComponent } from '@app/shared/confirm/confirm.component';
import { ConfirmDialogModel } from '@app/shared/confirm/confirm-dialog.model';
import { first } from 'rxjs/operators';

interface AddComment {
  text: string;
  attachmentTag: string | null;
  attachmentName: string | null;
  attachmentContentType: string | null;
}

@Component({
  selector: 'app-add-comment',
  templateUrl: './add-comment.component.html',
  styleUrls: ['./add-comment.component.scss'],
  standalone: false,
})
export class AddCommentComponent implements OnDestroy, OnInit {
  public model: AddComment;
  public videoVisible = true;
  public loading = false;
  public isEditing: boolean;

  private file: File | Blob | null = null;

  public previewUrl?: SafeUrl;
  private objectUrl?: string;

  public mimeType: 'image' | 'video' | 'pdf' | 'other' = 'other';

  context: CommentContext;
  comment: CommentDto | null;

  constructor(
    public dialogRef: MatDialogRef<AddCommentComponent>,
    @Inject(MAT_DIALOG_DATA) public commentModel: AddEditCommentModel,
    private workOrderCommentsService: WorkOrderCommentsService,
    private sanitizer: DomSanitizer,
    private loggingService: LoggingService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private store: Store,
  ) {
    this.context = commentModel.context;
    this.comment = commentModel.comment ?? null;
    this.isEditing = !!this.comment;

    this.model = {
      text: this.comment?.text ?? '',
      attachmentTag: this.comment?.attachment?.tag ?? null,
      attachmentName: null,
      attachmentContentType: null,
    };
  }
  ngOnInit(): void {
    if (!this.isEditing) {
      // ADD: always open camera first
      this.videoVisible = true;
      this.previewUrl = undefined;
      return;
    }

    // EDIT mode:
    if (this.comment?.attachment?.type) {
      // attachment exists → show preview first
      this.videoVisible = false;
      this.previewUrl = undefined;
    } else {
      // no attachment → behave like add
      this.videoVisible = true;
      this.previewUrl = undefined;
    }
  }

  ngOnDestroy(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }

  private classifyMime(m: string | null): 'image' | 'video' | 'pdf' | 'other' {
    if (!m) return 'other';
    if (m.startsWith('image/')) return 'image';
    if (m.startsWith('video/')) return 'video';
    if (m === 'application/pdf') return 'pdf';
    return 'other';
  }

  private setFileForUpload(fileOrBlob: File | Blob): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);

    const now = Date.now();
    this.file = fileOrBlob;

    const isFile = 'name' in fileOrBlob;
    this.model.attachmentName = isFile ? (fileOrBlob as File).name : `IMG_${now}`;
    this.model.attachmentContentType = fileOrBlob.type || 'application/octet-stream';

    this.mimeType = this.classifyMime(this.model.attachmentContentType);

    this.objectUrl = URL.createObjectURL(fileOrBlob);
    this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
  }

  getReferenceTypeKey() {
    return `workOrderComments.referenceType.${this.commentModel.context.referenceType}`;
  }

  onCloseCamera(blob: Blob): void {
    this.videoVisible = false;
    this.setFileForUpload(blob);
  }

  onShowCamera(): void {
    this.videoVisible = true;
  }

  onCancelCamera(): void {
    this.videoVisible = false;
    this.resetAttachment();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.setFileForUpload(input.files[0]);
      input.value = '';
    }
  }

  public resetAttachment(): void {
    this.file = null;
    this.model.attachmentName = null;
    this.model.attachmentContentType = null;
    this.model.attachmentTag = null;

    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);

    this.previewUrl = undefined;
    this.objectUrl = undefined;
  }

  confirm(): void {
    this.loading = true;

    if (this.isEditing && this.comment) {
      this.workOrderCommentsService
        .updateComment({
          commentId: this.comment.id,
          referenceId: this.commentModel.context.referenceId,
          referenceName: this.commentModel.context.referenceName,
          referenceType: this.commentModel.context.referenceType,
          text: this.model.text,
          attachmentName: this.model.attachmentName,
          attachmentTag: this.model.attachmentTag,
          file: this.file,
        })
        .then(
          () => {
            this.dialogRef.close(true);
            this.loading = false;
          },
          (error) => {
            this.loggingService.error(error);
            this.loading = false;
          },
        );
    } else {
      this.workOrderCommentsService
        .addComment({
          referenceId: this.commentModel.context.referenceId,
          referenceName: this.commentModel.context.referenceName,
          referenceType: this.commentModel.context.referenceType,
          text: this.model.text,
          attachmentName: this.model.attachmentName,
          attachmentTag: this.model.attachmentTag,
          attachmentContentType: this.model.attachmentContentType,
          file: this.file,
        })
        .then(
          () => {
            const isMaterial = this.commentModel.context.referenceType === 'MATERIAL';
            const isWorkLine = this.commentModel.context.referenceType === 'WORK_LINE';
            this.store.dispatch(new AddCommentAction(isMaterial, isWorkLine));

            this.dialogRef.close(true);
            this.loading = false;
          },
          (error) => {
            this.loggingService.error(error);
            this.loading = false;
          },
        );
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  onRetake(): void {
    // Remove local preview if any
    if (this.previewUrl) {
      this.resetAttachment();
    }

    // Hide server preview & show camera
    this.videoVisible = true;
  }

  deleteComment(): void {
    if (!this.comment) return;

    const message = this.translateService.instant('workOrderComments.delete.deleteNoteBy');
    const confirmMessage = this.comment.createdBy + ', ' + this.comment.createdTime;

    const dialog = this.dialog.open(ConfirmComponent, {
      width: '95vw',
      maxWidth: '520px',
      data: { message, confirmMessage } as ConfirmDialogModel,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (!result) return;

        this.workOrderCommentsService
          .deleteComment(
            this.comment!.id,
            this.comment!.referenceId,
            this.comment!.referenceName,
            this.comment!.referenceType,
          )
          .then(
            () => {
              const isMaterial = this.commentModel.context.referenceType === 'MATERIAL';
              const isWorkLine = this.commentModel.context.referenceType === 'WORK_LINE';
              this.store.dispatch(new DeleteCommentAction(isMaterial, isWorkLine));

              this.dialogRef.close(true);
              this.loading = false;
            },
            (error) => {
              this.loggingService.error(error);
              this.loading = false;
            },
          );
      });
  }
}
